import { useCallback, useState, useEffect } from 'react';
import { getSessionStorage } from 'services/storage/sessionStorage';
import Filter from '../../components/Filter';
import type { ColorFilter } from '@root/src/partials/Visualizer';

interface ToggleFilterCallback {
  (options: FilterOptions): void;
}

type Props = {
  colors: ColorFilter[];
  styles: string[];
  categories: string[];
  onToggle: ToggleFilterCallback;
};

export type FilterType = 'color' | 'style' | 'category';

export type FilterOptions = {
  [Key in FilterType]: string[];
};

export default function GalleryFilter(props: Props) {
  const { colors, styles, categories, onToggle } = props;

  const storageProvider = getSessionStorage();

  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    color: [],
    style: [],
    category: [],
  });

  const getOptionsFromStorage = useCallback((): FilterOptions | null => {
    try {
      const options = storageProvider.getItem('galleryFilters');

      return JSON.parse(options ?? 'null');
    } catch (e) {
      console.error(e);
      return null;
    }
  }, [storageProvider]);

  const putOptionsInStorage = useCallback(() => {
    try {
      storageProvider.setItem('galleryFilters', JSON.stringify(filterOptions));
    } catch (e) {
      console.error(e);
    }
  }, [storageProvider, filterOptions]);

  useEffect(() => {
    const options = getOptionsFromStorage();

    if (options) {
      setFilterOptions(options);
    }
  }, []);

  useEffect(() => {
    putOptionsInStorage();

    onToggle(filterOptions);
  }, [filterOptions]);

  const onOptionToggled = useCallback(
    (value: string, type: FilterType) => {
      const toggledOptions = filterOptions[type];

      const updatedOptions = toggledOptions.includes(value)
        ? toggledOptions.filter((option) => option !== value)
        : [...toggledOptions, value];

      setFilterOptions({
        ...filterOptions,
        [type]: updatedOptions,
      });
    },
    [filterOptions, setFilterOptions]
  );

  return (
    <div>
      <div className="mb-16">
        <h4 className="text-h4">Filters</h4>
      </div>

      <hr className="my-16 border-gray" />

      {styles.length > 0 && (
        <>
          <div>
            <p className="mb-16 text-sm text-gray-darker">Style</p>
          </div>
          <div className="grid grid-cols-3 gap-8 sm:grid-cols-2">
            {styles.map((style) => (
              <Filter
                id={`${style}FilterBtn`}
                key={style}
                onClick={() => {
                  onOptionToggled(style, 'style');
                }}
                isActive={filterOptions.style.includes(style)}
                filterLabel={style.charAt(0).toUpperCase() + style.substr(1).toLowerCase()}
              />
            ))}
          </div>
          <hr className="my-16 border-gray" />
        </>
      )}

      {colors.length > 0 && (
        <>
          <div>
            <p className="mb-16 text-sm text-gray-darker">Color</p>
          </div>
          <div className="grid grid-cols-3 gap-8 sm:grid-cols-2">
            {colors.map((color) => (
              <Filter
                id={`${color}FilterBtn`}
                key={color}
                onClick={() => {
                  onOptionToggled(color, 'color');
                }}
                isActive={filterOptions.color.includes(color)}
                filterColor={color}
                filterLabel={color.charAt(0).toUpperCase() + color.substr(1).toLowerCase()}
              />
            ))}
          </div>
          <hr className="my-16 border-gray" />
        </>
      )}

      {categories.length > 0 && (
        <>
          <div>
            <p className="mb-16 text-sm text-gray-darker">Category</p>
          </div>
          <div className="grid grid-cols-1 gap-8">
            {categories.map((category) => (
              <Filter
                id={`${category}FilterBtn`}
                key={category}
                onClick={() => {
                  onOptionToggled(category, 'category');
                }}
                isActive={filterOptions.category.includes(category)}
                filterLabel={category}
              />
            ))}
          </div>
          <hr className="my-16 border-gray" />
        </>
      )}
    </div>
  );
}
